import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { CanvasContext } from "../../../contexts/CanvasContext";
import ButtonComponent from "./ButtonComponent";
import ImageComponent from "./ImageComponent";
import HeaderComponent from "./HeaderComponent";
import ParagraphComponent from "./ParagraphComponent";
import IconComponent from "./IconComponent";
import LinkComponent from "./LinkComponent";
import CheckboxComponent from "./CheckboxComponent";
import RadioComponent from "./RadioComponent";
import ListComponent from "./ListComponent";
import TableComponent from "./TableComponent";
import VideoComponent from "./VideoComponent";
import TextInputComponent from "./TextComponent";
import RangeComponent from "./RangeComponent";
import PasswordInputComponent from "./PasswordInputComponent";
import EmailInputComponent from "./EmailInputComponent";
import NumberInputComponent from "./NumberInputComponent";
import DateInputComponent from "./DateInputComponent";
import ColorInputComponent from "./ColorInputComponent";
import FileInputComponent from "./FileInputComponent";
import AudioComponent from "./AudioComponent";
import DropDownComponent from "./DropDownComponent";
import MapComponent from "./MapComponent";
import Division from "./Division";
import AsideComponent from "./AsideComponent";
import ArticleComponent from "./ArticleComponent";
import MainComponent from "./MainComponent";
import FormComponent from "./FormComponent";
import Span from "./Span";
import Footer from "./Footer";
import HeaderTag from "./HeaderTag";
import Nav from "./Nav";
import HRTag from "./HRTag";
import BRTag from "./BRTag";
import OutputComponent from "./OutputComponent";
import IframeComponent from "./IframeComponent";
import Section from "./Section";

const Hgroup = ({ hgroup }) => {
  const hgroupRef = useRef(null);

  const { addElement, hoveredDivision, files, addCanvasDrop, addDivision } =
    useContext(CanvasContext);
  const isHovered = hgroup.id === hoveredDivision;
  const [isHgroupHovered, setIsHgroupHovered] = useState(false);
  const [isHgroupFocused, setIsHgroupFocused] = useState(false);
  const [isHgroupActive, setIsHgroupActive] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);

  const [, drop] = useDrop({
    accept: [
      "DroppableDiv",
      "DroppableButton",
      "DroppableImage",
      "DroppableHeading",
      "DroppableParagraph",
      "DroppableIcon",
      "DroppableLink",
      "DroppableCheckbox",
      "DroppableRadio",
      "DroppableList",
      "DroppableTable",
      "DroppableVideo",
      "DroppableAudio",
      "DroppableTextInput",
      "DroppableRangeInput",
      "DroppablePasswordInput",
      "DroppableEmailInput",
      "DroppableNumberInput",
      "DroppableDateInput",
      "DroppableColorInput",
      "DroppableFileInput",
      "DroppableDropDown",
      "DroppableMap",
      "DroppableNav",
      "DroppableHeader",
      "DroppableHGroup",
      "DroppableFooter",
      "DroppableSection",
      "DroppableSpan",
      "DroppableForm",
      "DroppableMain",
      "DroppableArticle",
      "DroppableAside",
      "DroppableHorizontalRule",
      "DroppableLineBreak",
      "DroppableIframe",
    ],
    drop: (item, monitor) => {
      if (!monitor.didDrop()) {
        if (item.type === "DroppableDiv") {
          addDivision(hgroup.id, item.name, item.type);
        } else if (
          item.type === "DroppableNav" ||
          item.type === "DroppableHeader" ||
          item.type === "DroppableHGroup" ||
          item.type === "DroppableFooter" ||
          item.type === "DroppableSection" ||
          item.type === "DroppableSpan" ||
          item.type === "DroppableForm" ||
          item.type === "DroppableMain" ||
          item.type === "DroppableArticle" ||
          item.type === "DroppableAside"
        ) {
          addCanvasDrop(hgroup.id, item.name, item.type);
        } else {
          addElement(hgroup.id, item.name, item.type);
        }
      }
    },
  });

  const constructGradientString = () => {
    const {
      backgroundGradientImage,
      gradientDirection,
      gradientShape,
      gradientPosition,
      startColor,
      endColor,
      gradientAngle,
      patternLength,
    } = hgroup;

    switch (backgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${gradientDirection || "to right"}, ${
          startColor || "#ff7e5f"
        }, ${endColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${gradientAngle || "45"}deg, ${
          startColor || "#606dbc"
        }, ${endColor || "#465298"} ${patternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"} ${
          patternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };
  const constructHoverGradientString = () => {
    const {
      hoveredbackgroundGradientImage,
      hoveredgradientDirection,
      hoveredgradientShape,
      hoveredgradientPosition,
      hoveredstartColor,
      hoveredendColor,
      hoveredgradientAngle,
      hoveredpatternLength,
    } = hgroup;

    switch (hoveredbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${hoveredgradientDirection || "to right"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${hoveredgradientShape || "circle"} at ${
          hoveredgradientPosition || "center"
        }, ${hoveredstartColor || "#ff7e5f"}, ${hoveredendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${hoveredgradientAngle || "45"}deg, ${
          hoveredstartColor || "#606dbc"
        }, ${hoveredendColor || "#465298"} ${hoveredpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          hoveredgradientShape || "circle"
        } at ${hoveredgradientPosition || "center"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"} ${hoveredpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructFocusedGradientString = () => {
    const {
      focusedbackgroundGradientImage,
      focusedgradientDirection,
      focusedgradientShape,
      focusedgradientPosition,
      focusedstartColor,
      focusedendColor,
      focusedgradientAngle,
      focusedpatternLength,
    } = hgroup;

    switch (focusedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${focusedgradientDirection || "to right"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${focusedgradientShape || "circle"} at ${
          focusedgradientPosition || "center"
        }, ${focusedstartColor || "#ff7e5f"}, ${focusedendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${focusedgradientAngle || "45"}deg, ${
          focusedstartColor || "#606dbc"
        }, ${focusedendColor || "#465298"} ${focusedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          focusedgradientShape || "circle"
        } at ${focusedgradientPosition || "center"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"} ${focusedpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructActivatedGradientString = () => {
    const {
      activatedbackgroundGradientImage,
      activatedgradientDirection,
      activatedgradientShape,
      activatedgradientPosition,
      activatedstartColor,
      activatedendColor,
      activatedgradientAngle,
      activatedpatternLength,
    } = hgroup;

    switch (activatedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${activatedgradientDirection || "to right"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${activatedgradientShape || "circle"} at ${
          activatedgradientPosition || "center"
        }, ${activatedstartColor || "#ff7e5f"}, ${
          activatedendColor || "#feb47b"
        })`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${
          activatedgradientAngle || "45"
        }deg, ${activatedstartColor || "#606dbc"}, ${
          activatedendColor || "#465298"
        } ${activatedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          activatedgradientShape || "circle"
        } at ${activatedgradientPosition || "center"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"} ${
          activatedpatternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };
  const generateKeyframes = () => {
    if (!hgroup.keyframes || hgroup.keyframes.length === 0) return null;

    return hgroup.keyframes.map((kf) => {
      const properties = {};
      let transform = "";

      Object.entries(kf.properties).forEach(([key, value]) => {
        // Remove 'KEY' prefix if present
        let processedKey = key.startsWith("KEY") ? key.slice(3) : key;

        // Handle transform properties
        if (
          processedKey === "scale" ||
          processedKey === "translateX" ||
          processedKey === "translateY" ||
          processedKey === "rotate" ||
          processedKey === "skewX" ||
          processedKey === "skewY"
        ) {
          if (value) {
            switch (processedKey) {
              case "scale":
                transform += `scale(${value}) `;
                break;
              case "translateX":
                transform += `translateX(${value}) `;
                break;
              case "translateY":
                transform += `translateY(${value}) `;
                break;
              case "rotate":
                transform += `rotate(${value}) `;
                break;
              case "skewX":
                transform += `skewX(${value}) `;
                break;
              case "skewY":
                transform += `skewY(${value}) `;
                break;
              default:
                break;
            }
          }
        } else {
          // Convert camelCase to kebab-case for other CSS properties
          properties[processedKey] = value;
        }
      });

      // Add transform property if it was built
      if (transform) {
        properties["transform"] = transform.trim();
      }

      return {
        offset: kf.position / 100,
        ...properties,
      };
    });
  };

  useEffect(() => {
    if (hgroupRef.current && hgroup.keyframes && hgroup.keyframes.length > 0) {
      const keyframes = generateKeyframes();
      const animation = hgroupRef.current.animate(keyframes, {
        duration: parseFloat(hgroup.animationDuration) * 1000 || 1000,
        delay: parseFloat(hgroup.animationDelay) * 1000 || 0,
        iterations:
          hgroup.animationIterationCount === "infinite"
            ? Infinity
            : parseFloat(hgroup.animationIterationCount) || 1,
        direction: hgroup.animationDirection || "normal",
        fill: hgroup.animationFillMode || "none",
        easing: hgroup.animationTimingFunction || "linear",
      });

      return () => animation.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hgroup.keyframes,
    hgroup.animationDuration,
    hgroup.animationDelay,
    hgroup.animationIterationCount,
    hgroup.animationDirection,
    hgroup.animationFillMode,
    hgroup.animationTimingFunction,
  ]);

  useEffect(() => {
    const fetchBackgroundImage = async () => {
      if (hgroup.backgroundImage && hgroup.backgroundImage.startsWith("./")) {
        const imageName = hgroup.backgroundImage.replace("./", "");
        const file = files.find((f) => f.name === imageName);
        if (file && file.data) {
          setBackgroundImageUrl(file.data);
        } else {
          console.error("Image not found in files:", imageName);
        }
      } else {
        setBackgroundImageUrl(hgroup.backgroundImage);
      }
    };

    fetchBackgroundImage();
  }, [hgroup.backgroundImage, files]);

  const focusedStyles =
    isHgroupFocused && hgroup.pseudoClass === "focus"
      ? {
          width: hgroup.focusedwidth || hgroup.width || "100%",
          height: hgroup.focusedheight || hgroup.height || "auto",

          padding: hgroup.focusedpadding || hgroup.padding || "8px",
          paddingTop:
            hgroup.focusedpaddingTop || hgroup.focusedpadding || "8px",
          paddingRight:
            hgroup.focusedpaddingRight || hgroup.focusedpadding || "8px",
          paddingBottom:
            hgroup.focusedpaddingBottom || hgroup.focusedpadding || "8px",
          paddingLeft:
            hgroup.focusedpaddingLeft || hgroup.focusedpadding || "8px",
          margin: hgroup.focusedmargin || hgroup.margin || "0px",
          marginTop: hgroup.focusedmarginTop || hgroup.focusedmargin || "0px",
          marginRight:
            hgroup.focusedmarginRight || hgroup.focusedmargin || "0px",
          marginBottom:
            hgroup.focusedmarginBottom || hgroup.focusedmargin || "0px",
          marginLeft: hgroup.focusedmarginLeft || hgroup.focusedmargin || "0px",

          borderTop:
            hgroup.focusedborderTop ||
            hgroup.focusedborder ||
            hgroup.border ||
            "1px solid black",
          borderRight:
            hgroup.focusedborderRight ||
            hgroup.focusedborder ||
            hgroup.border ||
            "1px solid black",
          borderBottom:
            hgroup.focusedborderBottom ||
            hgroup.focusedborder ||
            hgroup.border ||
            "1px solid black",
          borderLeft:
            hgroup.focusedborderLeft ||
            hgroup.focusedborder ||
            hgroup.border ||
            "1px solid black",
          borderSapcing:
            hgroup.focusedborderSpacing || hgroup.borderSpacing || "0px",
          borderCollapse:
            hgroup.focusedborderCollapse || hgroup.borderCollapse || "0px",
          borderRadius:
            hgroup.focusedborderRadius || hgroup.borderRadius || "0",
          outline: hgroup.focusedoutline || hgroup.outline || "none",

          position: hgroup.focusedposition || hgroup.position || "relative",
          top: hgroup.focusedtop || hgroup.top || "auto",
          right: hgroup.focusedright || hgroup.right || "auto",
          bottom: hgroup.focusedbottom || hgroup.bottom || "auto",
          left: hgroup.focusedleft || hgroup.left || "auto",
          zIndex: hgroup.focusedzIndex || hgroup.zIndex || "auto",
          boxShadow: hgroup.focusedboxShadow || hgroup.boxShadow || "none",
          opacity: hgroup.focusedOpacity || hgroup.opacity || 1,
          display: hgroup.focuseddisplay || hgroup.display || "block",
          flexDirection:
            hgroup.focusedflexDirection || hgroup.flexDirection || "column",
          flexWrap: hgroup.focusedflexWrap || "nowrap",
          order: hgroup.focusedorder || "0",
          flexGrow: hgroup.focusedflexGrow || "0",
          flexShrink: hgroup.focusedflexShrink || "1",
          flexBasis: hgroup.focusedflexBasis || "auto",

          gridTemplateColumns: hgroup.focusedgridTemplateColumns || "none",
          gridTemplateRows: hgroup.focusedgridTemplateRows || "none",
          gridArea: hgroup.focusedgridArea || "auto",
          gridAutoFlow: hgroup.focusedgridAutoFlow || "row",
          gridAutoColumns: hgroup.focusedgridAutoColumns || "auto",
          gridAutoRows: hgroup.focusedgridAutoRows || "auto",
          columnGap: hgroup.focusedcolumnGap || "0",
          rowGap: hgroup.focusedrowGap || "0",

          justifyContent: hgroup.focusedjustifyContent || "flex-start",
          alignItems: hgroup.focusedalignItems || "stretch",
          alignContent: hgroup.focusedalignContent || "stretch",
          alignSelf: hgroup.focusedalignSelf || "auto",
          // placeContent: hgroup.focusedplaceContent || "normal",
          // placeItems: hgroup.focusedplaceItems || "normal",
          justifyItems: hgroup.focusedjustifyItems || "stretch",
          justifySelf: hgroup.focusedjustifySelf || "auto",

          gap: hgroup.focusedgap || "0",
          visibility: hgroup.focusedvisibility || "visible",

          color: hgroup.focusedcolor || hgroup.color || "#000000",
          backgroundColor:
            hgroup.focusedbackgroundColor ||
            hgroup.backgroundColor ||
            "#ffffff",
          backgroundImage: (() => {
            if (hgroup.focusedbackgroundImage) {
              if (hgroup.focusedbackgroundImage.startsWith("./")) {
                const imageName = hgroup.focusedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${hgroup.focusedbackgroundImage})`;
            }
            if (hgroup.backgroundImage) {
              if (hgroup.backgroundImage.startsWith("./")) {
                const imageName = hgroup.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${hgroup.backgroundImage})`;
            }
            return (
              constructFocusedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            hgroup.focusedbackgroundPosition ||
            hgroup.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            hgroup.focusedbackgroundSize || hgroup.backgroundSize || "auto",
          backgroundRepeat:
            hgroup.focusedbackgroundRepeat ||
            hgroup.backgroundRepeat ||
            "repeat",
          backgroundAttachment:
            hgroup.focusedbackgroundAttachment ||
            hgroup.backgroundAttachment ||
            "scroll",

          fontFamily:
            hgroup.focusedfontFamily || hgroup.fontFamily || "inherit",
          fontSize: hgroup.focusedfontSize || hgroup.fontSize || "inherit",
          fontWeight: hgroup.focusedfontWeight || hgroup.fontWeight || "normal",
          fontStyle: hgroup.focusedfontStyle || hgroup.fontStyle || "normal",
          fontVariant:
            hgroup.focusedfontVariant || hgroup.fontVariant || "normal",
          lineHeight: hgroup.focusedlineHeight || hgroup.lineHeight || "normal",
          textShadow: hgroup.focusedtextShadow || hgroup.textShadow || "none",
          textAlign: hgroup.focusedtextAlign || hgroup.textAlign || "left",
          verticalAlign:
            hgroup.focusedverticalAlign || hgroup.verticalAlign || "baseline",
          textDecoration:
            hgroup.focusedtextDecoration || hgroup.textDecoration || "none",
          textTransform:
            hgroup.focusedtextTransform || hgroup.textTransform || "none",
          letterSpacing:
            hgroup.focusedletterSpacing || hgroup.letterSpacing || "normal",
          wordSpacing:
            hgroup.focusedwordSpacing || hgroup.wordSpacing || "normal",
          textIndent: hgroup.focusedtextIndent || hgroup.textIndent || "0",
          whiteSpace: hgroup.focusedwhiteSpace || hgroup.whiteSpace || "normal",
          textOverflow:
            hgroup.focusedtextOverflow || hgroup.textOverflow || "clip",
          direction: hgroup.focuseddirection || hgroup.direction || "ltr",
          unicodeBidi:
            hgroup.focusedunicodeBidi || hgroup.unicodeBidi || "normal",
          writingMode:
            hgroup.focusedwritingMode || hgroup.writingMode || "horizontal-tb",

          cursor: hgroup.focusedcursor || hgroup.cursor || "default",
          filter: hgroup.focusedfilter || hgroup.filter || "none",
          backdropFilter:
            hgroup.focusedbackdropFilter || hgroup.backdropFilter || "none",
          mixBlendMode:
            hgroup.focusedmixBlendMode || hgroup.mixBlendMode || "normal",
          aspectRatio:
            hgroup.focusedaspectRatio || hgroup.aspectRatio || "auto",

          transform: hgroup.focusedtransform || hgroup.transform || "none",
          transition: hgroup.focusedtransition || hgroup.transition || "none",
        }
      : {};

  const hoverStyles =
    isHgroupHovered && hgroup.pseudoClass === "hover"
      ? {
          width: hgroup.hoveredwidth || hgroup.width || "100%",
          height: hgroup.hoveredheight || hgroup.height || "auto",

          padding: hgroup.hoveredpadding || hgroup.padding || "8px",
          paddingTop:
            hgroup.hoveredpaddingTop || hgroup.hoveredpadding || "8px",
          paddingRight:
            hgroup.hoveredpaddingRight || hgroup.hoveredpadding || "8px",
          paddingBottom:
            hgroup.hoveredpaddingBottom || hgroup.hoveredpadding || "8px",
          paddingLeft:
            hgroup.hoveredpaddingLeft || hgroup.hoveredpadding || "8px",
          margin: hgroup.hoveredmargin || hgroup.margin || "0px",
          marginTop: hgroup.hoveredmarginTop || hgroup.hoveredmargin || "0px",
          marginRight:
            hgroup.hoveredmarginRight || hgroup.hoveredmargin || "0px",
          marginBottom:
            hgroup.hoveredmarginBottom || hgroup.hoveredmargin || "0px",
          marginLeft: hgroup.hoveredmarginLeft || hgroup.hoveredmargin || "0px",

          borderTop:
            hgroup.hoveredborderTop ||
            hgroup.hoveredborder ||
            hgroup.border ||
            "1px solid black",
          borderRight:
            hgroup.hoveredborderRight ||
            hgroup.hoveredborder ||
            hgroup.border ||
            "1px solid black",
          borderBottom:
            hgroup.hoveredborderBottom ||
            hgroup.hoveredborder ||
            hgroup.border ||
            "1px solid black",
          borderLeft:
            hgroup.hoveredborderLeft ||
            hgroup.hoveredborder ||
            hgroup.border ||
            "1px solid black",
          borderSapcing:
            hgroup.hoveredborderSpacing || hgroup.borderSpacing || "0px",
          borderCollapse:
            hgroup.hoveredborderCollapse || hgroup.borderCollapse || "0px",
          borderRadius:
            hgroup.hoveredborderRadius || hgroup.borderRadius || "0",
          outline: hgroup.hoveredoutline || hgroup.outline || "none",

          position: hgroup.hoveredposition || hgroup.position || "relative",
          top: hgroup.hoveredtop || hgroup.top || "auto",
          right: hgroup.hoveredright || hgroup.right || "auto",
          bottom: hgroup.hoveredbottom || hgroup.bottom || "auto",
          left: hgroup.hoveredleft || hgroup.left || "auto",
          zIndex: hgroup.hoveredzIndex || hgroup.zIndex || "auto",
          boxShadow: hgroup.hoveredboxShadow || hgroup.boxShadow || "none",
          opacity: hgroup.hoveredOpacity || hgroup.opacity || 1,
          display: hgroup.hovereddisplay || hgroup.display || "block",
          flexDirection:
            hgroup.hoveredflexDirection || hgroup.flexDirection || "column",
          flexWrap: hgroup.hoveredflexWrap || "nowrap",
          order: hgroup.hoveredorder || "0",
          flexGrow: hgroup.hoveredflexGrow || "0",
          flexShrink: hgroup.hoveredflexShrink || "1",
          flexBasis: hgroup.hoveredflexBasis || "auto",

          gridTemplateColumns: hgroup.hoveredgridTemplateColumns || "none",
          gridTemplateRows: hgroup.hoveredgridTemplateRows || "none",
          gridArea: hgroup.hoveredgridArea || "auto",
          gridAutoFlow: hgroup.hoveredgridAutoFlow || "row",
          gridAutoColumns: hgroup.hoveredgridAutoColumns || "auto",
          gridAutoRows: hgroup.hoveredgridAutoRows || "auto",
          columnGap: hgroup.hoveredcolumnGap || "0",
          rowGap: hgroup.hoveredrowGap || "0",

          justifyContent: hgroup.hoveredjustifyContent || "flex-start",
          alignItems: hgroup.hoveredalignItems || "stretch",
          alignContent: hgroup.hoveredalignContent || "stretch",
          alignSelf: hgroup.hoveredalignSelf || "auto",
          // placeContent: hgroup.hoveredplaceContent || "normal",
          // placeItems: hgroup.hoveredplaceItems || "normal",
          justifyItems: hgroup.hoveredjustifyItems || "stretch",
          justifySelf: hgroup.hoveredjustifySelf || "auto",

          gap: hgroup.hoveredgap || "0",
          visibility: hgroup.hoveredvisibility || "visible",

          color: hgroup.hoveredcolor || hgroup.color || "#000000",
          backgroundColor:
            hgroup.hoveredbackgroundColor ||
            hgroup.backgroundColor ||
            "#ffffff",
          backgroundImage: (() => {
            if (hgroup.hoveredbackgroundImage) {
              if (hgroup.hoveredbackgroundImage.startsWith("./")) {
                const imageName = hgroup.hoveredbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${hgroup.hoveredbackgroundImage})`;
            }
            if (hgroup.backgroundImage) {
              if (hgroup.backgroundImage.startsWith("./")) {
                const imageName = hgroup.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${hgroup.backgroundImage})`;
            }
            return (
              constructHoverGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            hgroup.hoveredbackgroundPosition ||
            hgroup.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            hgroup.hoveredbackgroundSize || hgroup.backgroundSize || "auto",
          backgroundRepeat:
            hgroup.hoveredbackgroundRepeat ||
            hgroup.backgroundRepeat ||
            "repeat",
          backgroundAttachment:
            hgroup.hoveredbackgroundAttachment ||
            hgroup.backgroundAttachment ||
            "scroll",

          fontFamily:
            hgroup.hoveredfontFamily || hgroup.fontFamily || "inherit",
          fontSize: hgroup.hoveredfontSize || hgroup.fontSize || "inherit",
          fontWeight: hgroup.hoveredfontWeight || hgroup.fontWeight || "normal",
          fontStyle: hgroup.hoveredfontStyle || hgroup.fontStyle || "normal",
          fontVariant:
            hgroup.hoveredfontVariant || hgroup.fontVariant || "normal",
          lineHeight: hgroup.hoveredlineHeight || hgroup.lineHeight || "normal",
          textShadow: hgroup.hoveredtextShadow || hgroup.textShadow || "none",
          textAlign: hgroup.hoveredtextAlign || hgroup.textAlign || "left",
          verticalAlign:
            hgroup.hoveredverticalAlign || hgroup.verticalAlign || "baseline",
          textDecoration:
            hgroup.hoveredtextDecoration || hgroup.textDecoration || "none",
          textTransform:
            hgroup.hoveredtextTransform || hgroup.textTransform || "none",
          letterSpacing:
            hgroup.hoveredletterSpacing || hgroup.letterSpacing || "normal",
          wordSpacing:
            hgroup.hoveredwordSpacing || hgroup.wordSpacing || "normal",
          textIndent: hgroup.hoveredtextIndent || hgroup.textIndent || "0",
          whiteSpace: hgroup.hoveredwhiteSpace || hgroup.whiteSpace || "normal",
          textOverflow:
            hgroup.hoveredtextOverflow || hgroup.textOverflow || "clip",
          direction: hgroup.hovereddirection || hgroup.direction || "ltr",
          unicodeBidi:
            hgroup.hoveredunicodeBidi || hgroup.unicodeBidi || "normal",
          writingMode:
            hgroup.hoveredwritingMode || hgroup.writingMode || "horizontal-tb",

          cursor: hgroup.hoveredcursor || hgroup.cursor || "default",
          filter: hgroup.hoveredfilter || hgroup.filter || "none",
          backdropFilter:
            hgroup.hoveredbackdropFilter || hgroup.backdropFilter || "none",
          mixBlendMode:
            hgroup.hoveredmixBlendMode || hgroup.mixBlendMode || "normal",
          aspectRatio:
            hgroup.hoveredaspectRatio || hgroup.aspectRatio || "auto",

          transform: hgroup.hoveredtransform || hgroup.transform || "none",
          transition: hgroup.hoveredtransition || hgroup.transition || "none",
        }
      : {};

  const activeStyles =
    isHgroupActive && hgroup.pseudoClass === "active"
      ? {
          width: hgroup.activatedwidth || hgroup.width || "100%",
          height: hgroup.hoveredheight || hgroup.height || "auto",

          padding: hgroup.activatedpadding || hgroup.padding || "8px",
          paddingTop:
            hgroup.activatedpaddingTop || hgroup.activatedpadding || "8px",
          paddingRight:
            hgroup.activatedpaddingRight || hgroup.activatedpadding || "8px",
          paddingBottom:
            hgroup.activatedpaddingBottom || hgroup.activatedpadding || "8px",
          paddingLeft:
            hgroup.activatedpaddingLeft || hgroup.activatedpadding || "8px",
          margin: hgroup.activatedmargin || hgroup.margin || "0px",
          marginTop:
            hgroup.activatedmarginTop || hgroup.activatedmargin || "0px",
          marginRight:
            hgroup.activatedmarginRight || hgroup.activatedmargin || "0px",
          marginBottom:
            hgroup.activatedmarginBottom || hgroup.activatedmargin || "0px",
          marginLeft:
            hgroup.activatedmarginLeft || hgroup.activatedmargin || "0px",

          borderTop:
            hgroup.activatedborderTop ||
            hgroup.activatedborder ||
            hgroup.border ||
            "1px solid black",
          borderRight:
            hgroup.activatedborderRight ||
            hgroup.activatedborder ||
            hgroup.border ||
            "1px solid black",
          borderBottom:
            hgroup.activatedborderBottom ||
            hgroup.activatedborder ||
            hgroup.border ||
            "1px solid black",
          borderLeft:
            hgroup.activatedborderLeft ||
            hgroup.activatedborder ||
            hgroup.border ||
            "1px solid black",
          borderSapcing:
            hgroup.activatedborderSpacing || hgroup.borderSpacing || "0px",
          borderCollapse:
            hgroup.activatedborderCollapse || hgroup.borderCollapse || "0px",
          borderRadius:
            hgroup.activatedborderRadius || hgroup.borderRadius || "0",
          outline: hgroup.activatedoutline || hgroup.outline || "none",

          position: hgroup.activatedposition || hgroup.position || "relative",
          top: hgroup.activatedtop || hgroup.top || "auto",
          right: hgroup.activatedright || hgroup.right || "auto",
          bottom: hgroup.activatedbottom || hgroup.bottom || "auto",
          left: hgroup.activatedleft || hgroup.left || "auto",
          zIndex: hgroup.activatedzIndex || hgroup.zIndex || "auto",

          boxShadow: hgroup.activatedboxShadow || hgroup.boxShadow || "none",
          opacity: hgroup.activatedOpacity || hgroup.opacity || 1,
          display: hgroup.activateddisplay || hgroup.display || "block",
          flexDirection:
            hgroup.activatedflexDirection || hgroup.flexDirection || "column",
          flexWrap: hgroup.activatedflexWrap || "nowrap",
          order: hgroup.activatedorder || "0",
          flexGrow: hgroup.activatedflexGrow || "0",
          flexShrink: hgroup.activatedflexShrink || "1",
          flexBasis: hgroup.activatedflexBasis || "auto",

          gridTemplateColumns: hgroup.activatedgridTemplateColumns || "none",
          gridTemplateRows: hgroup.activatedgridTemplateRows || "none",
          gridArea: hgroup.activatedgridArea || "auto",
          gridAutoFlow: hgroup.activatedgridAutoFlow || "row",
          gridAutoColumns: hgroup.activatedgridAutoColumns || "auto",
          gridAutoRows: hgroup.activatedgridAutoRows || "auto",
          columnGap: hgroup.activatedcolumnGap || "0",
          rowGap: hgroup.activatedrowGap || "0",

          justifyContent: hgroup.activatedjustifyContent || "flex-start",
          alignItems: hgroup.activatedalignItems || "stretch",
          alignContent: hgroup.activatedalignContent || "stretch",
          alignSelf: hgroup.activatedalignSelf || "auto",
          // placeContent: hgroup.activatedplaceContent || "normal",
          // placeItems: hgroup.activatedplaceItems || "normal",
          justifyItems: hgroup.activatedjustifyItems || "stretch",
          justifySelf: hgroup.activatedjustifySelf || "auto",

          gap: hgroup.activatedgap || "0",
          visibility: hgroup.activatedvisibility || "visible",

          color: hgroup.activatedcolor || hgroup.color || "#000000",
          backgroundColor:
            hgroup.activatedbackgroundColor ||
            hgroup.backgroundColor ||
            "#ffffff",
          backgroundImage: (() => {
            if (hgroup.activatedbackgroundImage) {
              if (hgroup.activatedbackgroundImage.startsWith("./")) {
                const imageName = hgroup.activatedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${hgroup.activatedbackgroundImage})`;
            }
            if (hgroup.backgroundImage) {
              if (hgroup.backgroundImage.startsWith("./")) {
                const imageName = hgroup.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${hgroup.backgroundImage})`;
            }
            return (
              constructActivatedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            hgroup.activatedbackgroundPosition ||
            hgroup.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            hgroup.activatedbackgroundSize || hgroup.backgroundSize || "auto",
          backgroundRepeat:
            hgroup.activatedbackgroundRepeat ||
            hgroup.backgroundRepeat ||
            "repeat",
          backgroundAttachment:
            hgroup.activatedbackgroundAttachment ||
            hgroup.backgroundAttachment ||
            "scroll",

          fontFamily:
            hgroup.activatedfontFamily || hgroup.fontFamily || "inherit",
          fontSize: hgroup.activatedfontSize || hgroup.fontSize || "inherit",
          fontWeight:
            hgroup.activatedfontWeight || hgroup.fontWeight || "normal",
          fontStyle: hgroup.activatedfontStyle || hgroup.fontStyle || "normal",
          fontVariant:
            hgroup.activatedfontVariant || hgroup.fontVariant || "normal",
          lineHeight:
            hgroup.activatedlineHeight || hgroup.lineHeight || "normal",
          textShadow: hgroup.activatedtextShadow || hgroup.textShadow || "none",
          textAlign: hgroup.activatedtextAlign || hgroup.textAlign || "left",
          verticalAlign:
            hgroup.activatedverticalAlign || hgroup.verticalAlign || "baseline",
          textDecoration:
            hgroup.activatedtextDecoration || hgroup.textDecoration || "none",
          textTransform:
            hgroup.activatedtextTransform || hgroup.textTransform || "none",
          letterSpacing:
            hgroup.activatedletterSpacing || hgroup.letterSpacing || "normal",
          wordSpacing:
            hgroup.activatedwordSpacing || hgroup.wordSpacing || "normal",
          textIndent: hgroup.activatedtextIndent || hgroup.textIndent || "0",
          whiteSpace:
            hgroup.activatedwhiteSpace || hgroup.whiteSpace || "normal",
          textOverflow:
            hgroup.activatedtextOverflow || hgroup.textOverflow || "clip",
          direction: hgroup.activateddirection || hgroup.direction || "ltr",
          unicodeBidi:
            hgroup.activatedunicodeBidi || hgroup.unicodeBidi || "normal",
          writingMode:
            hgroup.activatedwritingMode ||
            hgroup.writingMode ||
            "horizontal-tb",

          cursor: hgroup.activatedcursor || hgroup.cursor || "default",
          filter: hgroup.activatedfilter || hgroup.filter || "none",
          backdropFilter:
            hgroup.activatedbackdropFilter || hgroup.backdropFilter || "none",
          mixBlendMode:
            hgroup.activatedmixBlendMode || hgroup.mixBlendMode || "normal",
          aspectRatio:
            hgroup.activatedaspectRatio || hgroup.aspectRatio || "auto",

          transform: hgroup.activatedtransform || hgroup.transform || "none",
          transition: hgroup.activatedtransition || hgroup.transition || "none",
        }
      : {};

  const hgroupStyle = {
    width: hgroup.width || "100%",
    height: hgroup.height || "auto",
    padding: hgroup.padding || "8px",
    paddingTop: hgroup.paddingTop || hgroup.padding || "8px",
    paddingRight: hgroup.paddingRight || hgroup.padding || "8px",
    paddingBottom: hgroup.paddingBottom || hgroup.padding || "8px",
    paddingLeft: hgroup.paddingLeft || hgroup.padding || "8px",
    margin: hgroup.margin || "0px",
    marginTop: hgroup.marginTop || hgroup.margin || "0px",
    marginRight: hgroup.marginRight || hgroup.margin || "0px",
    marginBottom: hgroup.marginBottom || hgroup.margin || "0px",
    marginLeft: hgroup.marginLeft || hgroup.margin || "0px",

    borderTop: hgroup.borderTop || hgroup.border || "1px solid #000000",
    borderRight: hgroup.borderRight || hgroup.border || "1px solid #000000",
    borderBottom: hgroup.borderBottom || hgroup.border || "1px solid #000000",
    borderLeft: hgroup.borderLeft || hgroup.border || "1px solid #000000",

    borderSapcing: hgroup.borderSpacing || "0px",
    borderCollapse: hgroup.borderCollapse || "0px",
    borderRadius: hgroup.borderRadius || "0",
    boxSizing: hgroup.boxSizing || "border-box",

    position: hgroup.position || "relative",
    top: hgroup.top || "auto",
    right: hgroup.right || "auto",
    bottom: hgroup.bottom || "auto",
    left: hgroup.left || "auto",
    zIndex: hgroup.zIndex || "auto",

    display: hgroup.display || "block",
    flexDirection: hgroup.flexDirection || "column",
    flexWrap: hgroup.flexWrap || "nowrap",
    order: hgroup.order || "0",
    flexGrow: hgroup.flexGrow || "0",
    flexShrink: hgroup.flexShrink || "1",
    flexBasis: hgroup.flexBasis || "auto",

    gridTemplateColumns: hgroup.gridTemplateColumns || "none",
    gridTemplateRows: hgroup.gridTemplateRows || "none",
    gridArea: hgroup.gridArea || "auto",
    gridAutoFlow: hgroup.gridAutoFlow || "row",
    gridAutoColumns: hgroup.gridAutoColumns || "auto",
    gridAutoRows: hgroup.gridAutoRows || "auto",
    columnGap: hgroup.columnGap || "0",
    rowGap: hgroup.rowGap || "0",

    justifyContent: hgroup.justifyContent || "flex-start",
    alignItems: hgroup.alignItems || "stretch",
    alignContent: hgroup.alignContent || "stretch",
    alignSelf: hgroup.alignSelf || "auto",
    // placeContent: hgroup.placeContent || "normal",
    // placeItems: hgroup.placeItems || "normal",
    justifyItems: hgroup.justifyItems || "stretch",
    justifySelf: hgroup.justifySelf || "auto",

    gap: hgroup.gap || "0",
    visibility: hgroup.visibility || "visible",

    color: hgroup.color || "#000000",
    backgroundImage: backgroundImageUrl
      ? `url(${backgroundImageUrl})`
      : constructGradientString(),

    backgroundPosition: hgroup.backgroundPosition || "0% 0%",
    backgroundSize: hgroup.backgroundSize || "auto",
    backgroundRepeat: hgroup.backgroundRepeat || "repeat",
    backgroundAttachment: hgroup.backgroundAttachment || "scroll",
    boxShadow: hgroup.boxShadow || "none",
    filter: hgroup.filter || "none",
    backdropFilter: hgroup.backdropFilter || "none",
    mixBlendMode: hgroup.mixBlendMode || "normal",
    aspectRatio: hgroup.aspectRatio || "auto",

    resize: hgroup.resize || "none",
    userSelect: hgroup.userSelect || "auto",
    overflow: hgroup.resize && hgroup.resize !== "none" ? "auto" : "visible",
    overflowX: hgroup.overflowX || "hidden",
    overflowY: hgroup.overflowY || "hidden",

    fontSize: hgroup.fontSize || "inherit",
    fontFamily: hgroup.fontFamily || "inherit",
    fontWeight: hgroup.fontWeight || "normal",
    fontStyle: hgroup.fontStyle || "normal",
    fontVariant: hgroup.fontVariant || "normal",
    lineHeight: hgroup.lineHeight || "normal",
    textAlign: hgroup.textAlign || "left",
    textDecoration: hgroup.textDecoration || "none",
    textTransform: hgroup.textTransform || "none",
    textShadow: hgroup.textShadow || "none",
    verticalAlign: hgroup.verticalAlign || "baseline",
    letterSpacing: hgroup.letterSpacing || "normal",
    wordSpacing: hgroup.wordSpacing || "normal",
    textIndent: hgroup.textIndent || "0",
    whiteSpace: hgroup.whiteSpace || "normal",
    textOverflow: hgroup.textOverflow || "clip",
    direction: hgroup.direction || "ltr",
    unicodeBidi: hgroup.unicodeBidi || "normal",
    writingMode: hgroup.writingMode || "horizontal-tb",
    cursor: hgroup.cursor || "default",
    outline: hgroup.outline || "none",

    backgroundColor: isHovered
      ? "#ADD8E6"
      : hgroup.backgroundColor || "#ffffff",

    listStyleType: hgroup.listStyleType || "disc",
    listStyleImage: hgroup.listStyleImage
      ? `url(${hgroup.listStyleImage})`
      : "none",
    listStylePosition: hgroup.listStylePosition || "outside",

    transform: hgroup.transform || "none",
    transition:
      hgroup.transition || "transform 0.3s ease, background-color 0.3s ease",

    ...hoverStyles,
    ...focusedStyles,
    ...activeStyles,
  };

  const combinedRef = useMemo(
    () => (node) => {
      hgroupRef.current = node;
      drop(node);
    },
    [drop]
  );

  return (
    <header
      ref={combinedRef}
      style={hgroupStyle}
      onMouseEnter={() => setIsHgroupHovered(true)}
      onMouseLeave={() => setIsHgroupHovered(false)}
      onFocus={() => setIsHgroupFocused(true)}
      onBlur={() => setIsHgroupFocused(false)}
      onMouseDown={() => setIsHgroupActive(true)}
      onMouseUp={() => setIsHgroupActive(false)}
      tabIndex={0}
    >
      {hgroup.children &&
        hgroup.children.map((child) => {
          switch (child.name) {
            case "Button":
              return <ButtonComponent key={child.id} button={child} />;
            case "Image":
              return <ImageComponent key={child.id} image={child} />;
            case "Heading":
              return <HeaderComponent key={child.id} header={child} />;
            case "Paragraph":
              return <ParagraphComponent key={child.id} paragraph={child} />;
            case "Icon":
              return <IconComponent key={child.id} icon={child} />;
            case "Link":
              return <LinkComponent key={child.id} link={child} />;
            case "Checkbox":
              return <CheckboxComponent key={child.id} checkbox={child} />;
            case "Radio":
              return <RadioComponent key={child.id} radio={child} />;
            case "List":
              return <ListComponent key={child.id} list={child} />;
            case "Table":
              return <TableComponent key={child.id} table={child} />;
            case "Video":
              return <VideoComponent key={child.id} video={child} />;
            case "Text":
              return <TextInputComponent key={child.id} textInput={child} />;
            case "Range":
              return <RangeComponent key={child.id} range={child} />;
            case "Password":
              return (
                <PasswordInputComponent key={child.id} passwordInput={child} />
              );
            case "Email":
              return <EmailInputComponent key={child.id} emailInput={child} />;
            case "Number":
              return (
                <NumberInputComponent key={child.id} numberInput={child} />
              );
            case "Date":
              return <DateInputComponent key={child.id} dateInput={child} />;
            case "Color":
              return <ColorInputComponent key={child.id} colorInput={child} />;
            case "File":
              return <FileInputComponent key={child.id} fileInput={child} />;
            case "Audio":
              return <AudioComponent key={child.id} audio={child} />;
            case "DropDown":
              return <DropDownComponent key={child.id} dropdown={child} />;
            case "Output":
              return <OutputComponent key={child.id} output={child} />;
            case "Map":
              return <MapComponent key={child.id} map={child} />;
            case "Nav":
              return <Nav key={child.id} nav={child} />;
            case "Header":
              return <HeaderTag key={child.id} headertag={child} />;
            case "HGroup":
              return <Hgroup key={child.id} hgroup={child} />;
            case "Footer":
              return <Footer key={child.id} footer={child} />;
            case "Section":
              return <Section key={child.id} section={child} />;
            case "Span":
              return <Span key={child.id} span={child} />;
            case "Form":
              return <FormComponent key={child.id} form={child} />;
            case "Main":
              return <MainComponent key={child.id} main={child} />;
            case "Article":
              return <ArticleComponent key={child.id} article={child} />;
            case "Aside":
              return <AsideComponent key={child.id} aside={child} />;
            case "HorizontalRule":
              return <HRTag key={child.id} div={child} />;
            case "LineBreak":
              return <BRTag key={child.id} div={child} />;
            case "Iframe":
              return <IframeComponent key={child.id} div={child} />;
            default:
              return <Division key={child.id} div={child} />;
          }
        })}
    </header>
  );
};

export default Hgroup;
